/**
 * "Does it repeat?" in plain words: never / every day / every week on
 * chosen days / a few times a week / every month. A repeating task is a
 * routine: tick it and the next one appears on its own.
 */
import { Repeat } from "lucide-react";
import { Select } from "@/components/ui";
import { DayToggles, ChoicePills } from "./fields";
import { repeatLabel, weekday } from "./lib";
import type { Recurrence } from "./types";

type Mode = "none" | "daily" | "weekly" | "times" | "monthly";

function modeOf(r: Recurrence | null): Mode {
  if (!r) return "none";
  if (r.freq === "daily") return "daily";
  if (r.freq === "monthly") return "monthly";
  if (r.times && r.times > 1 && !(r.days && r.days.length)) return "times";
  return "weekly";
}

export function RepeatPicker({
  value, onChange, dueDate,
}: { value: Recurrence | null; onChange: (r: Recurrence | null) => void; dueDate?: string }) {
  const mode = modeOf(value);
  const n = value?.interval && value.interval > 1 ? value.interval : 1;

  const pick = (m: Mode | null) => {
    if (!m || m === "none") return onChange(null);
    if (m === "daily") return onChange({ freq: "daily", interval: 1 });
    if (m === "monthly") return onChange({ freq: "monthly", interval: 1 });
    if (m === "times") return onChange({ freq: "weekly", interval: 1, times: 3 });
    // Weekly: start from the due day's weekday so it reads naturally.
    return onChange({ freq: "weekly", interval: 1, days: [dueDate ? weekday(dueDate) : new Date().getDay()] });
  };

  return (
    <div className="space-y-2.5">
      <ChoicePills<Mode>
        value={mode}
        onChange={(m) => pick(m)}
        options={[
          { value: "none", label: "Just once" },
          { value: "daily", label: "Every day" },
          { value: "weekly", label: "Every week" },
          { value: "times", label: "A few times a week" },
          { value: "monthly", label: "Every month" },
        ]}
      />

      {mode === "weekly" && value && (
        <div className="space-y-2 rounded-xl border border-zinc-800/80 bg-zinc-900/40 p-3">
          <div className="text-[11px] text-zinc-500">On these days</div>
          <DayToggles value={value.days ?? []} onChange={(days) => onChange({ ...value, days, times: undefined })} />
          {!(value.days && value.days.length) && (
            <label className="flex items-center gap-2 text-xs text-zinc-400">
              Every
              <Select value={String(n)} onChange={(e) => onChange({ ...value, interval: Number(e.target.value) })} className="h-8 w-20">
                {[1, 2, 3, 4].map((x) => <option key={x} value={x}>{x}</option>)}
              </Select>
              {n === 1 ? "week" : "weeks"}
            </label>
          )}
        </div>
      )}

      {mode === "times" && value && (
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          <Select value={String(value.times ?? 2)} onChange={(e) => onChange({ ...value, times: Number(e.target.value) })} className="h-8 w-20">
            {[2, 3, 4, 5, 6].map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
          times a week, on whichever days suit
        </label>
      )}

      {(mode === "daily" || mode === "monthly") && value && (
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          Every
          <Select value={String(n)} onChange={(e) => onChange({ ...value, interval: Number(e.target.value) })} className="h-8 w-20">
            {(mode === "daily" ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 6]).map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
          {mode === "daily" ? (n === 1 ? "day" : "days") : n === 1 ? "month" : "months"}
        </label>
      )}

      {value && (
        <div className="flex items-center gap-1.5 text-[11px] text-indigo-300/90">
          <Repeat className="h-3 w-3" />
          Repeats {repeatLabel(value)}. Tick it and the next one appears by itself.
        </div>
      )}
    </div>
  );
}
